import { supabase, run } from './db';
import { cached } from './local/cache';
import { queueable } from './local/queued';

/**
 * Os textos livres dos relatórios de visita do SEBRAE.
 *
 * Cada um dos oito relatórios tem ATIVIDADES DESENVOLVIDAS e OBSERVAÇÕES em
 * texto livre, e às vezes uma data de visita própria. Nada disso é obrigatório:
 * o gerador calcula tudo a partir das etapas lançadas, e o que está gravado
 * aqui vence.
 *
 * Por isso a linha só nasce quando alguém edita. Um serviço SEBRAE em que o
 * consultor não mudou nenhuma frase não tem nenhuma linha aqui, e imprime os
 * oito do mesmo jeito.
 */

/** Os textos já gravados de um serviço, indexados pelo número do relatório. */
export const fetchTextosDeVisita = (serviceId) =>
  cached(`relatorio-visita:${serviceId}`, () => run(supabase
    .from('relatorio_visita_textos')
    .select('*')
    .eq('service_id', serviceId)
    .order('numero')));

/**
 * Grava o texto de UM relatório.
 *
 * Enfileirável: é digitação de campo, e o consultor escreve a observação na
 * fazenda, onde frequentemente não há sinal. A emenda vai para a lista do
 * serviço para que o texto recém-digitado apareça na tela mesmo offline.
 */
export const salvarTextoDeVisita = queueable('relatorio-visita.save',
  (linha) => run(supabase
    .from('relatorio_visita_textos')
    .upsert(linha, { onConflict: 'service_id,numero' })),
  {
    patchOf: (linha) => ({
      chave: `relatorio-visita:${linha.service_id}`,
      tipo: 'atualiza',
      id: linha.id,
      campos: linha,
      ordenarPor: 'numero',
    }),
  });

/**
 * Monta a linha a partir do que está na tela.
 *
 * Texto em branco vira `null`, não string vazia: a coluna nula significa "use
 * o texto calculado", e string vazia significaria "imprima nada" — apagar o
 * que escreveu tem que devolver o padrão, não deixar o relatório mudo.
 */
export const montarTextoDeVisita = ({ id, serviceId, laboratoryId, numero, dataVisita, atividades, observacoes }) => ({
  ...(id ? { id } : {}),
  service_id: serviceId,
  laboratory_id: laboratoryId,
  numero,
  data_visita: dataVisita || null,
  atividades: (atividades || '').trim() || null,
  observacoes: (observacoes || '').trim() || null,
});

/** Os textos por número, para o gerador consultar sem varrer a lista. */
export const textosPorNumero = (linhas = []) =>
  Object.fromEntries((linhas || []).map(l => [Number(l.numero), l]));

/**
 * Apaga o texto gravado, devolvendo o relatório ao texto calculado.
 *
 * Apagar a LINHA, e não gravar vazio: a ausência de linha é o que significa
 * "use o que os dados dizem". Gravar strings vazias deixaria o relatório mudo
 * e ainda congelado.
 *
 * Existe porque texto salvo vence o calculado — e sem uma saída, o relatório
 * ficava preso ao número de embriões do dia em que alguém salvou, mesmo depois
 * de a etapa mudar.
 */
export const apagarTextoDeVisita = queueable('relatorio-visita.delete',
  (id) => run(supabase.from('relatorio_visita_textos').delete().eq('id', id)),
  {
    patchOf: (id, serviceId) => (serviceId ? {
      chave: `relatorio-visita:${serviceId}`, tipo: 'remove', id,
    } : null),
  });
